/**
 * Acquisition attribution capture.
 *
 * On the first session we parse UTM params + gclid from window.location.search, the referrer,
 * and the landing path; persist them to localStorage (stable re-attribution across sessions);
 * and emit `session_started` once per tab session (sessionStorage guard).
 *
 * utm_source / utm_campaign / gclid are also stamped onto account_created at signup.
 */

import { logEvent } from '../analytics/analytics'
import { errorLog } from '../utils/log-helper'

const STORAGE_KEY = 'rsly_acquisition'
const SESSION_GUARD_KEY = 'rsly_session_started'

export interface Acquisition {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  gclid?: string
  referrer?: string
  landing_path?: string
}

function readStored(): Acquisition | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Acquisition) : null
  } catch {
    return null
  }
}

function persist(acq: Acquisition): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(acq))
  } catch {
    // storage unavailable — attribution just won't be stable across sessions
  }
}

/**
 * Parse acquisition params from the current URL + referrer. Only keys with values are included.
 */
function parseFromLocation(): Acquisition {
  const acq: Acquisition = {}
  try {
    const params = new URLSearchParams(window.location.search)
    const utmSource = params.get('utm_source')
    const utmMedium = params.get('utm_medium')
    const utmCampaign = params.get('utm_campaign')
    const utmTerm = params.get('utm_term')
    const utmContent = params.get('utm_content')
    const gclid = params.get('gclid')
    if (utmSource) acq.utm_source = utmSource
    if (utmMedium) acq.utm_medium = utmMedium
    if (utmCampaign) acq.utm_campaign = utmCampaign
    if (utmTerm) acq.utm_term = utmTerm
    if (utmContent) acq.utm_content = utmContent
    if (gclid) acq.gclid = gclid
    if (typeof document !== 'undefined' && document.referrer) {
      acq.referrer = document.referrer
    }
    acq.landing_path = window.location.pathname
  } catch (error) {
    errorLog('[acquisition] parse failed', error)
  }
  return acq
}

/**
 * Returns the persisted acquisition attribution (first-touch), if any.
 * Used to stamp utm_source/utm_campaign/gclid onto account_created.
 */
export function getStoredAcquisition(): Acquisition | null {
  return readStored()
}

/**
 * Capture acquisition on first session and emit `session_started` once per tab session.
 *
 * First-touch: we only persist attribution the first time we see it (stable re-attribution).
 * We still refresh landing_path/referrer for the current visit's session_started payload.
 */
export function captureSessionStart(): void {
  try {
    // Once-per-tab-session guard (mirrors app_opened / home_loaded).
    let alreadyStarted = false
    try {
      alreadyStarted = sessionStorage.getItem(SESSION_GUARD_KEY) === '1'
      sessionStorage.setItem(SESSION_GUARD_KEY, '1')
    } catch {
      // sessionStorage unavailable — best-effort, may re-emit
    }
    if (alreadyStarted) {
      return
    }

    const current = parseFromLocation()
    const stored = readStored()

    // First-touch attribution: persist only if we don't already have UTM/gclid stored.
    const hasStoredAttribution = !!(stored?.utm_source ?? stored?.gclid)
    const hasCurrentAttribution = !!(current.utm_source ?? current.gclid)
    if (!hasStoredAttribution && hasCurrentAttribution) {
      persist(current)
    } else if (!stored) {
      // Persist referrer/landing even without UTM so first-touch referrer is retained.
      persist(current)
    }

    logEvent('session_started', current)
  } catch (error) {
    errorLog('[acquisition] captureSessionStart failed', error)
  }
}
